<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AutomationEventsAPI from 'dashboard/api/automationEvents';
import AutomationEventDetailModal from './AutomationEventDetailModal.vue';
import {
  buildTimelineQueries,
  mergeTimelineEvents,
} from './automationTimeline';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
  limit: {
    type: Number,
    default: 10,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const events = ref([]);
const isLoading = ref(false);
const error = ref('');
const isDetailOpen = ref(false);
const selectedEvent = ref(null);
const isDetailLoading = ref(false);
let activeFetchRequestId = 0;
let activeDetailRequestId = 0;

const resolvedContactId = computed(() => props.contactId || props.contact?.id);
const contactEmail = computed(
  () => props.contact?.email || props.contact?.emailAddress
);
const contactPhone = computed(
  () => props.contact?.phone_number || props.contact?.phoneNumber
);

const hasLookupKey = computed(
  () => resolvedContactId.value || contactEmail.value || contactPhone.value
);

const formatDate = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');

  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel = status => {
  switch (status) {
    case 'completed':
      return t('AUTOMATION_EVENTS.STATUSES.COMPLETED');
    case 'failed':
      return t('AUTOMATION_EVENTS.STATUSES.FAILED');
    case 'ignored':
      return t('AUTOMATION_EVENTS.STATUSES.IGNORED');
    case 'manual_action':
      return t('AUTOMATION_EVENTS.STATUSES.MANUAL_ACTION');
    case 'processing':
      return t('AUTOMATION_EVENTS.STATUSES.PROCESSING');
    case 'received':
      return t('AUTOMATION_EVENTS.STATUSES.RECEIVED');
    default:
      return status;
  }
};

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const eventTitle = event =>
  [event.source, event.event_type].filter(Boolean).join(' - ');

const eventProduct = event =>
  event.product_name ||
  event.product_code ||
  t('AUTOMATION_EVENTS.EMPTY_VALUE');

const stepsText = event => {
  const summary = event.steps_summary || {};
  const total = summary.total || 0;
  if (!total) return t('AUTOMATION_EVENTS.TIMELINE.NO_STEPS');

  const byStatus = summary.by_status || {};
  return Object.entries(byStatus)
    .map(([status, count]) =>
      t('AUTOMATION_EVENTS.TIMELINE.STEP_STATUS_COUNT', {
        count,
        status: statusLabel(status),
      })
    )
    .join(' - ');
};

const transactionLabel = event => `#${event.transaction_id}`;

const fetchEvents = async () => {
  activeFetchRequestId += 1;
  const requestId = activeFetchRequestId;
  const contactId = resolvedContactId.value;
  const email = contactEmail.value;
  const phone = contactPhone.value;

  if (!hasLookupKey.value) {
    events.value = [];
    error.value = '';
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = '';

  const queries = buildTimelineQueries({
    contactId,
    email,
    phone,
  });

  try {
    const results = [];
    const primaryQuery = contactId ? queries.shift() : null;
    if (primaryQuery) {
      try {
        results.push({
          status: 'fulfilled',
          value: await AutomationEventsAPI.list({
            ...primaryQuery,
            per_page: props.limit,
          }),
        });
      } catch (reason) {
        results.push({ status: 'rejected', reason });
      }
    }

    results.push(
      ...(await Promise.allSettled(
        queries.map(params =>
          AutomationEventsAPI.list({ ...params, per_page: props.limit })
        )
      ))
    );
    const fulfilled = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value.data?.data || []);
    if (requestId !== activeFetchRequestId) return;

    events.value = mergeTimelineEvents(fulfilled, contactId).slice(
      0,
      props.limit
    );
    if (!fulfilled.length) {
      error.value = t('AUTOMATION_EVENTS.ERROR');
    } else if (results.some(result => result.status === 'rejected')) {
      error.value = t('AUTOMATION_EVENTS.TIMELINE.PARTIAL_ERROR');
    }
  } catch {
    if (requestId !== activeFetchRequestId) return;

    events.value = [];
    error.value = t('AUTOMATION_EVENTS.ERROR');
  } finally {
    if (requestId === activeFetchRequestId) isLoading.value = false;
  }
};

const openDetail = async event => {
  activeDetailRequestId += 1;
  const requestId = activeDetailRequestId;
  isDetailOpen.value = true;
  isDetailLoading.value = true;
  selectedEvent.value = event;
  try {
    const { data } = await AutomationEventsAPI.show(event.id);
    if (requestId !== activeDetailRequestId) return;

    selectedEvent.value = data?.automation_event || event;
  } catch {
    if (requestId !== activeDetailRequestId) return;

    selectedEvent.value = null;
    isDetailOpen.value = false;
    error.value = t('AUTOMATION_EVENTS.ERROR');
  } finally {
    if (requestId === activeDetailRequestId) isDetailLoading.value = false;
  }
};

onMounted(fetchEvents);

watch(
  () => [resolvedContactId.value, contactEmail.value, contactPhone.value],
  () => {
    activeDetailRequestId += 1;
    selectedEvent.value = null;
    isDetailOpen.value = false;
    isDetailLoading.value = false;
    fetchEvents();
  }
);
</script>

<template>
  <section
    class="border border-n-weak bg-n-solid-1"
    :class="compact ? 'mx-2 mb-2 rounded-xl' : 'rounded-xl'"
  >
    <header class="flex items-center justify-between gap-3 px-3 py-2.5">
      <div class="flex min-w-0 items-center gap-2">
        <span class="i-ri-flow-chart size-4 shrink-0 text-n-slate-11" />
        <h3 class="truncate text-sm font-medium text-n-slate-12">
          {{ t('AUTOMATION_EVENTS.TIMELINE.TITLE') }}
        </h3>
      </div>
      <button
        type="button"
        class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2"
        :aria-label="t('AUTOMATION_EVENTS.ACTIONS.REFRESH')"
        @click="fetchEvents"
      >
        <span class="i-ri-refresh-line size-3.5" />
      </button>
    </header>

    <div v-if="isLoading" class="space-y-2 border-t border-n-weak px-3 py-3">
      <div
        v-for="index in 3"
        :key="index"
        class="h-16 animate-pulse rounded-md bg-n-alpha-2"
      />
    </div>

    <p
      v-else-if="error && !events.length"
      class="border-t border-n-weak px-3 py-4 text-xs text-n-ruby-11"
    >
      {{ error }}
    </p>

    <div
      v-else-if="!hasLookupKey"
      class="border-t border-n-weak px-3 py-4 text-xs text-n-slate-10"
    >
      {{ t('AUTOMATION_EVENTS.TIMELINE.NO_CONTACT_KEY') }}
    </div>

    <div
      v-else-if="!events.length"
      class="border-t border-n-weak px-3 py-4 text-xs text-n-slate-10"
    >
      {{ t('AUTOMATION_EVENTS.TIMELINE.EMPTY') }}
    </div>

    <div v-else class="border-t border-n-weak">
      <p v-if="error" class="px-3 pt-3 text-xs text-n-amber-11">
        {{ error }}
      </p>
      <article
        v-for="event in events"
        :key="event.id"
        class="border-b border-n-weak px-3 py-3 last:border-0"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="truncate text-xs font-semibold text-n-slate-12">
              {{ eventTitle(event) }}
            </div>
            <div
              class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-n-slate-10"
            >
              <span>{{ formatDate(event.received_at) }}</span>
              <span>{{ eventProduct(event) }}</span>
              <span v-if="event.launch_id">
                {{
                  t('AUTOMATION_EVENTS.TIMELINE.LAUNCH', {
                    launch: event.launch_id,
                  })
                }}
              </span>
              <span v-if="event.transaction_id">
                {{ transactionLabel(event) }}
              </span>
            </div>
          </div>
          <span
            class="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium"
            :class="statusClass(event.status)"
          >
            {{ statusLabel(event.status) }}
          </span>
        </div>

        <p class="mt-1.5 text-[11px] text-n-slate-11">
          {{ stepsText(event) }}
        </p>
        <p
          v-if="event.current_error"
          class="mt-1.5 line-clamp-2 text-[11px] text-n-ruby-11"
        >
          {{ event.current_error }}
        </p>
        <button
          type="button"
          class="mt-2 inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
          @click="openDetail(event)"
        >
          <span class="i-ri-file-search-line size-3.5" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.DETAIL') }}
        </button>
      </article>
    </div>

    <AutomationEventDetailModal
      v-model="isDetailOpen"
      :event="selectedEvent"
      :is-loading="isDetailLoading"
    />
  </section>
</template>
